import { currentNavMenu, navDestinationIsSelected } from './navSelection';
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DisplaySettingsOutlinedIcon from '@mui/icons-material/DisplaySettingsOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import { colors, getSidebarChrome } from '../../theme/tokens';
import { PaddiqLogo } from '../brand/PaddiqLogo';
import { useInterfacePreferences } from '../GridlineProvider';
import { ThemeModeSelect } from '../settings/ThemeModeSelect';

export const SIDEBAR_WIDTH          = 196;
export const SIDEBAR_COLLAPSED_WIDTH = 52;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'paddiq:sidebar:collapsed';

export interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  external?: boolean;
  tooltip?: string;
  badge?: string | number;
  /** Shows a bell + count when greater than zero. */
  notificationCount?: number;
  /** Shows a bell without a count while discovery matching is still running. */
  notificationPending?: boolean;
  dividerBefore?: boolean;
  disableNavigation?: boolean;
  children?: Array<{
    label: string;
    path: string;
    icon?: React.ReactNode;
    activePaths?: string[];
  }>;
}

export interface TeamOption {
  id:   string;
  name: string;
}

export interface SidebarDriverProfile {
  name: string;
  role: string;
  initials: string;
  avatarUrl?: string | null;
  avatar?: React.ReactNode;
  kartNumber?: string | null;
  kartModel?: string | null;
  connected?: boolean;
  profilePath?: string;
}

export interface UserAccountMenuItem {
  id: string;
  label: string;
}

export interface GlobalSearchResult {
  id: string;
  group: string;
  label: string;
  description?: string;
  path: string;
  icon?: React.ReactNode;
  external?: boolean;
}

interface AppShellProps {
  navItems:      NavItem[];
  currentPath:   string;
  onNavigate:    (path: string) => void;
  teams?:        TeamOption[];
  currentTeamId?: string;
  onTeamChange?: (teamId: string) => void;
  teamSubtitle?: string;
  userName:    string;
  userRole:    string;
  userInitials:string;
  sidebarDriver?: SidebarDriverProfile;
  onUserAction?: () => void;
  userActionIcon?: React.ReactNode;
  userActionLabel?: string;
  userMenuItems?: UserAccountMenuItem[];
  onUserMenuSelect?: (itemId: string) => void;
  onLogout?: () => void;
  logoutLabel?: string;
  onFeedback?: () => void;
  feedbackIcon?: React.ReactNode;
  feedbackLabel?: string;
  onHelp?: () => void;
  onSettings?: () => void;
  headerActions?: React.ReactNode;
  /** Desktop utilities can keep account/settings/update actions in the navigation footer. */
  controlsPlacement?: 'header' | 'sidebar';
  /** Small desktop navigation can use only the shared global search. */
  showNavigationSearch?: boolean;
  globalSearchResults?: GlobalSearchResult[];
  globalSearchLoading?: boolean;
  globalSearchError?: string;
  onGlobalSearchQueryChange?: (query: string) => void;
  appTitle?:   string;
  brandIconSrc?: string;
  /** Allows desktop wrappers to reserve native title-bar space without changing web layout. */
  viewportHeight?: string;
  /** Hides the in-sidebar chevron when a desktop wrapper owns the sidebar toggle. */
  showSidebarCollapseControl?: boolean;
  /** Increment to request a sidebar toggle from an external window-chrome control. */
  sidebarToggleTrigger?: number;
  /** Reports the resolved desktop sidebar state to wrappers that extend it into native chrome. */
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  /** Desktop apps can own their navigation preference independently of the web app. */
  sidebarPreferenceKey?: string;
  defaultSidebarCollapsed?: boolean;
  mainContentRef?: React.Ref<HTMLDivElement>;
  children:    React.ReactNode;
}

function loadSidebarCollapsedPreference(key = SIDEBAR_COLLAPSED_STORAGE_KEY, fallback = true) {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
}

function persistSidebarCollapsedPreference(collapsed: boolean, key = SIDEBAR_COLLAPSED_STORAGE_KEY) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, String(collapsed));
  } catch {
    // Keep navigation usable when local preferences cannot be saved.
  }
}

/**
 * AppShell — matches the Paddiq design mockup sidebar exactly.
 *
 * • PADDIQ logo + lightning bolt brand mark
 * • Team dropdown selector below logo
 * • Collapsible nav with icons + labels, active state in primary purple
 * • Collapse button at the bottom
 * • User profile footer
 *
 * Shared across web, desktop (Electron), and mobile (React Native Web).
 */
export function AppShell({
  navItems,
  currentPath,
  onNavigate,
  teams = [],
  currentTeamId,
  onTeamChange,
  userName,
  userRole,
  userInitials,
  sidebarDriver,
  onUserAction,
  userActionIcon,
  userActionLabel = 'Open user settings',
  userMenuItems,
  onUserMenuSelect,
  onLogout,
  logoutLabel = 'Log out',
  onFeedback,
  feedbackIcon,
  feedbackLabel = 'Feedback',
  onHelp,
  onSettings,
  headerActions,
  controlsPlacement = 'header',
  showNavigationSearch = true,
  globalSearchResults = [],
  globalSearchLoading = false,
  globalSearchError,
  onGlobalSearchQueryChange,
  appTitle = 'PADDIQ',
  brandIconSrc = '/ICON.png',
  viewportHeight = '100dvh',
  showSidebarCollapseControl = true,
  sidebarToggleTrigger,
  onSidebarCollapsedChange,
  sidebarPreferenceKey,
  defaultSidebarCollapsed = true,
  mainContentRef,
  children,
}: AppShellProps) {
  const { preferences: interfacePreferences } = useInterfacePreferences();
  const searchResultsId = useId();
  const headerSearchRef = useRef<HTMLDivElement>(null);
  const muiTheme   = useTheme();
  const isDark     = muiTheme.palette.mode === 'dark';
  const chrome     = getSidebarChrome(isDark);
  const isMobile   = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [collapsedPref, setCollapsedPref] = useState(() => {
    if (sidebarPreferenceKey) return loadSidebarCollapsedPreference(sidebarPreferenceKey, defaultSidebarCollapsed);
    if (interfacePreferences.sidebarDefault === 'open') return false;
    if (interfacePreferences.sidebarDefault === 'collapsed') return true;
    return loadSidebarCollapsedPreference();
  });
  const collapsed = collapsedPref && !isMobile;
  const [navQuery, setNavQuery] = useState('');
  const [headerQuery, setHeaderQuery] = useState('');
  const [openNavGroups, setOpenNavGroups] = useState<Record<string, boolean>>({});
  const [collapsedNavMenuAnchor, setCollapsedNavMenuAnchor] = useState<null | HTMLElement>(null);
  const [collapsedNavMenuItem, setCollapsedNavMenuItem] = useState<NavItem | null>(null);
  const activeNavMenuItem = currentNavMenu(navItems, collapsedNavMenuItem);
  const [headerSearchAnchor, setHeaderSearchAnchor] = useState<null | HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sidebarToggleTriggerRef = useRef(sidebarToggleTrigger);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(accountMenuAnchor);

  const defaultAccountMenuItems: UserAccountMenuItem[] = userMenuItems ?? [
    { id: 'profile', label: 'Profile & avatar' },
    { id: 'interface', label: 'Interface' },
    { id: 'measurements', label: 'Measurements' },
    { id: 'password', label: 'Change password' },
  ];

  useEffect(() => {
    if (sidebarPreferenceKey) return;
    if (interfacePreferences.sidebarDefault === 'open') {
      setCollapsedPref(false);
    } else if (interfacePreferences.sidebarDefault === 'collapsed') {
      setCollapsedPref(true);
    }
  }, [interfacePreferences.sidebarDefault, sidebarPreferenceKey]);

  useEffect(() => {
    if (
      sidebarToggleTrigger === undefined
      || sidebarToggleTriggerRef.current === sidebarToggleTrigger
    ) {
      return;
    }

    sidebarToggleTriggerRef.current = sidebarToggleTrigger;
    setCollapsedNavMenuAnchor(null);
    setCollapsedNavMenuItem(null);
    setCollapsedPref((value) => {
      const next = !value;
      persistSidebarCollapsedPreference(next, sidebarPreferenceKey);
      return next;
    });
  }, [sidebarToggleTrigger, sidebarPreferenceKey]);

  useEffect(() => {
    onSidebarCollapsedChange?.(collapsed);
  }, [collapsed, onSidebarCollapsedChange]);

  function openAccountMenu(event: React.MouseEvent<HTMLElement>) {
    setAccountMenuAnchor(event.currentTarget);
  }

  function closeAccountMenu() {
    setAccountMenuAnchor(null);
  }

  function handleAccountMenuSelect(itemId: string) {
    closeAccountMenu();
    if (onUserMenuSelect) {
      onUserMenuSelect(itemId);
      return;
    }

    onNavigate(`${profilePath}?section=${itemId}`);
  }

  function handleLogoutClick() {
    closeAccountMenu();
    onLogout?.();
  }

  function openInterfaceSettings() {
    if (onSettings) {
      onSettings();
    } else if (onUserAction) {
      onUserAction();
    } else {
      handleAccountMenuSelect('interface');
    }
  }

  function navigateFromSearch(path: string, external?: boolean) {
    setHeaderSearchAnchor(null);
    setHeaderQuery('');
    onGlobalSearchQueryChange?.('');
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    onNavigate(path);
    if (isMobile) setMobileOpen(false);
  }

  function toggleSidebarCollapsed() {
    setCollapsedNavMenuAnchor(null);
    setCollapsedNavMenuItem(null);
    setCollapsedPref((value) => {
      const next = !value;
      persistSidebarCollapsedPreference(next, sidebarPreferenceKey);
      return next;
    });
  }

  function closeCollapsedNavMenu() {
    setCollapsedNavMenuAnchor(null);
    setCollapsedNavMenuItem(null);
  }

  function expandSidebarForSearch() {
    setCollapsedPref(false);
    persistSidebarCollapsedPreference(false, sidebarPreferenceKey);
    window.setTimeout(() => searchInputRef.current?.focus(), 220);
  }

  useEffect(() => {
    function focusNavigationSearch(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return;
      }

      event.preventDefault();
      if (!showNavigationSearch) {
        headerSearchRef.current?.querySelector('input')?.focus();
        return;
      }
      if (collapsed) {
        setCollapsedPref(false);
        persistSidebarCollapsedPreference(false, sidebarPreferenceKey);
        window.setTimeout(() => searchInputRef.current?.focus(), 220);
        return;
      }

      searchInputRef.current?.focus();
    }

    window.addEventListener('keydown', focusNavigationSearch);
    return () => window.removeEventListener('keydown', focusNavigationSearch);
  }, [collapsed, showNavigationSearch]);

  function accountMenuIcon(itemId: string) {
    switch (itemId) {
      case 'account':
      case 'profile':
        return <PersonOutlineOutlinedIcon sx={{ fontSize: 16 }} />;
      case 'measurements':
        return <StraightenOutlinedIcon sx={{ fontSize: 16 }} />;
      case 'interface':
        return <DisplaySettingsOutlinedIcon sx={{ fontSize: 16 }} />;
      case 'password':
        return <LockOutlinedIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  }

  const sidebarW = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const selectedTeam = teams.find((team) => team.id === currentTeamId) ?? teams[0];
  const normalizedNavQuery = navQuery.trim().toLowerCase();
  const normalizedHeaderQuery = headerQuery.trim().toLowerCase();
  const visibleNavItems = normalizedNavQuery
    ? navItems.filter((item) => (
        item.label.toLowerCase().includes(normalizedNavQuery)
        || item.children?.some((child) => child.label.toLowerCase().includes(normalizedNavQuery))
      ))
    : navItems;
  const pageSearchResults: GlobalSearchResult[] = normalizedHeaderQuery
    ? navItems
        .flatMap((item) => [
          { id: `page:${item.path}`, group: 'Pages', label: item.label, path: item.path, icon: item.icon, external: item.external },
          ...(item.children ?? []).map((child) => ({
            id: `page:${child.path}`,
            group: 'Pages',
            label: child.label,
            path: child.path,
            icon: child.icon ?? item.icon,
            external: false,
          })),
        ])
        .filter((item) => item.label.toLowerCase().includes(normalizedHeaderQuery))
        .slice(0, 5)
    : [];
  const headerSearchResults = [...globalSearchResults, ...pageSearchResults]
    .filter((result, index, results) => results.findIndex((candidate) => candidate.path === result.path && candidate.label === result.label) === index)
    .slice(0, 20);
  const activeDriver = sidebarDriver ?? {
    name: userName,
    role: userRole,
    initials: userInitials,
    profilePath: '/settings',
  };
  const profilePath = activeDriver.profilePath ?? '/settings';
  const showAccountMenu = Boolean(onUserMenuSelect || onLogout || profilePath === '/settings');
  const profileAvatar = activeDriver.avatar ? (
    <Box sx={{ width: 28, height: 28, flexShrink: 0, '& .MuiAvatar-root': { width: '100%', height: '100%', fontSize: '0.62rem' } }}>
      {activeDriver.avatar}
    </Box>
  ) : (
    <Avatar src={activeDriver.avatarUrl ?? undefined} sx={{ width: 28, height: 28, fontSize: '0.62rem', fontWeight: 900, flexShrink: 0, bgcolor: chrome.profileAvatarBg, color: '#FFFFFF', border: `1px solid ${chrome.profileAvatarBorder}` }}>
      {activeDriver.initials}
    </Avatar>
  );

  // ─── Sidebar content ────────────────────────────────────────────────────────
  const accountControls = (
          <Box sx={{ ml: controlsPlacement === 'sidebar' ? 0 : 'auto', display: 'flex', alignItems: 'center', flexDirection: controlsPlacement === 'sidebar' && collapsed ? 'column' : 'row', gap: 0.25, flexShrink: 0, width: controlsPlacement === 'sidebar' ? '100%' : undefined }}>
            {headerActions}
            {onHelp ? (
              <Tooltip title="Help & guides">
                <IconButton onClick={onHelp} aria-label="Help & guides" sx={{ width: 32, height: 32, borderRadius: '7px', color: chrome.textSecondary }}>
                  <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : null}
            {onFeedback ? (
              <Tooltip title={feedbackLabel}>
                <IconButton onClick={onFeedback} aria-label={feedbackLabel} sx={{ width: 32, height: 32, borderRadius: '7px', color: chrome.textSecondary, '& svg': { fontSize: 18 } }}>
                  {feedbackIcon ?? <MoreHorizIcon />}
                </IconButton>
              </Tooltip>
            ) : null}
            {onSettings || onUserAction || showAccountMenu ? (
              <Tooltip title={onSettings ? 'Interface settings' : userActionLabel}>
                <IconButton
                  onClick={openInterfaceSettings}
                  aria-label="Interface settings"
                  sx={{ width: 32, height: 32, borderRadius: '7px', color: chrome.textSecondary }}
                >
                  {userActionIcon ?? <DisplaySettingsOutlinedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            ) : null}
            <Button
              onClick={showAccountMenu ? openAccountMenu : () => onNavigate(profilePath)}
              aria-label={showAccountMenu ? 'Open profile menu' : 'View profile'}
              sx={{ minWidth: 0, height: 36, ml: controlsPlacement === 'sidebar' ? 0 : 0.25, px: controlsPlacement === 'sidebar' && collapsed ? 0 : { xs: 0.4, sm: 0.65 }, gap: 0.7, borderRadius: '8px', color: chrome.text, textTransform: 'none' }}
            >
              {profileAvatar}
              <Typography noWrap sx={{ display: controlsPlacement === 'sidebar' ? (collapsed ? 'none' : 'block') : { xs: 'none', lg: 'block' }, maxWidth: controlsPlacement === 'sidebar' ? 66 : 140, fontSize: '0.72rem', fontWeight: 750 }}>
                {activeDriver.name}
              </Typography>
              {showAccountMenu ? <KeyboardArrowDownRoundedIcon sx={{ display: controlsPlacement === 'sidebar' ? 'none' : { xs: 'none', sm: 'block' }, fontSize: 15, color: chrome.textMuted }} /> : null}
            </Button>
          </Box>
  );

  const sidebarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: viewportHeight,
        minHeight: 0,
        width: sidebarW,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        color: chrome.text,
      }}
    >
      {/* ── Workspace header ──────────────────────────────────────────────── */}
      <Box
        aria-label={appTitle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 0.75 : '12px',
          py: '9px',
          minHeight: 50,
          flexShrink: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        {collapsed && showSidebarCollapseControl ? (
          <Tooltip title="Expand navigation" placement="right">
            <IconButton
              onClick={toggleSidebarCollapsed}
              aria-label="Expand navigation"
              sx={{ width: 36, height: 36, borderRadius: '7px' }}
            >
              <PaddiqLogo variant="sidebar" compact iconSrc={brandIconSrc} />
            </IconButton>
          </Tooltip>
        ) : collapsed ? (
          <PaddiqLogo variant="sidebar" compact iconSrc={brandIconSrc} />
        ) : (
          <>
            <Box
              component="button"
              type="button"
              disabled={!selectedTeam || !onTeamChange}
              onClick={selectedTeam && onTeamChange ? () => onTeamChange(selectedTeam.id) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                minWidth: 0,
                flex: 1,
                cursor: selectedTeam && onTeamChange ? 'pointer' : 'default',
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                textAlign: 'left',
              }}
            >
              <PaddiqLogo variant="sidebar" compact iconSrc={brandIconSrc} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 650, color: chrome.text }} noWrap>
                {selectedTeam?.name ?? appTitle}
              </Typography>
              {selectedTeam && onTeamChange ? <MoreHorizIcon sx={{ fontSize: 14, color: chrome.textMuted }} /> : null}
            </Box>
            {!isMobile && showSidebarCollapseControl ? (
              <Tooltip title="Collapse navigation" placement="right">
                <IconButton
                  onClick={toggleSidebarCollapsed}
                  aria-label="Collapse navigation"
                  sx={{ width: 28, height: 28, ml: 0.5, color: chrome.textMuted, borderRadius: '6px' }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        )}
      </Box>

      {/* ── Navigation search ─────────────────────────────────────────────── */}
      {showNavigationSearch && (collapsed ? (
        <Tooltip title="Search navigation" placement="right">
          <IconButton
            onClick={expandSidebarForSearch}
            aria-label="Search navigation"
            sx={{
              width: 32,
              height: 32,
              mx: '10px',
              mb: '8px',
              color: chrome.textSecondary,
              borderRadius: '7px',
              border: `1px solid ${chrome.border}`,
              bgcolor: isDark ? '#111113' : '#FFFFFF',
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ px: '8px', mb: '8px', flexShrink: 0 }}>
          <Box
            sx={{
              height: 32,
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              px: '7px',
              borderRadius: '7px',
              border: `1px solid ${chrome.border}`,
              bgcolor: isDark ? '#111113' : '#FFFFFF',
              color: chrome.textMuted,
              '&:focus-within': { borderColor: isDark ? 'rgba(167,139,250,0.36)' : '#C4B5E9' },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            <InputBase
              inputRef={searchInputRef}
              value={navQuery}
              onChange={(event) => setNavQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setNavQuery('');
                  event.currentTarget.blur();
                }
              }}
              placeholder="Search navigation..."
              inputProps={{ 'aria-label': 'Search navigation' }}
              sx={{
                minWidth: 0,
                flex: 1,
                color: chrome.text,
                fontSize: '0.75rem',
                '& input::placeholder': { color: chrome.textMuted, opacity: 1 },
              }}
            />
            <Box
              aria-hidden
              sx={{
                minWidth: 18,
                height: 18,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '4px',
                border: `1px solid ${chrome.border}`,
                color: chrome.textMuted,
                fontSize: '0.625rem',
              }}
            >
              /
            </Box>
          </Box>
        </Box>
      ))}

      {/* ── Nav items ────────────────────────────────────────────────────── */}
      <List
        sx={{
          px: '8px',
          py: 0,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        disablePadding
      >
        {visibleNavItems.map((item) => {
          const canNavigate = !item.disableNavigation;
          const currentPathname = currentPath.split('?')[0];
          const itemPathname = item.path.split('?')[0];
          const isSelected = !item.external && (
            item.path === '/'
              ? currentPath === '/'
              : item.children?.length
                ? currentPathname === itemPathname || currentPathname?.startsWith(`${itemPathname}/`)
                : currentPath.startsWith(item.path)
          );

          const hasNotification = (item.notificationCount !== undefined && item.notificationCount > 0) || item.notificationPending;
          const hasChildren = Boolean(item.children?.length);
          const groupOpen = !collapsed && (
            normalizedNavQuery.length > 0
            || openNavGroups[item.path] !== false && (openNavGroups[item.path] === true || isSelected)
          );

          return (
            <React.Fragment key={item.path}>
              {item.dividerBefore && <Box sx={{ height: 10 }} />}

              <Tooltip title={collapsed ? item.tooltip ?? item.label : item.tooltip ?? ''} placement="right">
                <ListItemButton
                  selected={isSelected}
                  aria-label={item.label}
                  aria-expanded={hasChildren && !collapsed ? groupOpen : undefined}
                  aria-haspopup={hasChildren && collapsed ? 'menu' : undefined}
                  onClick={canNavigate ? (event) => {
                    if (hasChildren && collapsed) {
                      setCollapsedNavMenuAnchor(event.currentTarget);
                      setCollapsedNavMenuItem(item);
                      return;
                    }
                    if (item.external) {
                      window.open(item.path, '_blank', 'noopener,noreferrer');
                    } else {
                      onNavigate(item.path);
                    }
                    if (hasChildren && !collapsed) {
                      setOpenNavGroups((groups) => ({ ...groups, [item.path]: !groupOpen }));
                    }
                    if (isMobile && !hasChildren) setMobileOpen(false);
                  } : undefined}
                  aria-disabled={!canNavigate ? true : undefined}
                  tabIndex={canNavigate ? undefined : -1}
                  disableRipple={!canNavigate}
                  sx={{
                    borderRadius: '6px',
                    mb: '2px',
                    py: 0,
                    px: collapsed ? 0.75 : '8px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    minHeight: 34,
                    cursor: canNavigate ? 'pointer' : 'default',
                    color: isSelected ? chrome.navActiveText : chrome.navText,
                    '& .MuiListItemIcon-root': {
                      color: 'inherit',
                    },
                    '&:hover': {
                      bgcolor: canNavigate ? chrome.navHover : 'transparent',
                    },
                    '&.Mui-selected': {
                      bgcolor: chrome.navActiveBg,
                      color: chrome.navActiveText,
                      boxShadow: 'none',
                      '& .MuiListItemIcon-root': {
                        color: chrome.navActiveIcon,
                      },
                      '& .MuiListItemText-root, & .MuiListItemText-root .MuiTypography-root': {
                        color: chrome.navActiveText,
                      },
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: canNavigate ? chrome.navActiveHover : chrome.navActiveBg,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 'auto' : 27,
                      opacity: 1,
                      '& svg': { fontSize: 17, strokeWidth: 1.7 },
                    }}
                  >
                    {hasNotification && collapsed ? (
                      item.notificationCount !== undefined && item.notificationCount > 0 ? (
                        <Badge
                          badgeContent={item.notificationCount}
                          color="info"
                          overlap="circular"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.62rem',
                              height: 16,
                              minWidth: 16,
                              fontWeight: 700,
                            },
                          }}
                        >
                          {item.icon}
                        </Badge>
                      ) : (
                        <Badge
                          variant="dot"
                          color="info"
                          overlap="circular"
                          sx={{
                            '& .MuiBadge-badge': {
                              height: 8,
                              minWidth: 8,
                              borderRadius: '50%',
                            },
                          }}
                        >
                          {item.icon}
                        </Badge>
                      )
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>

                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={isSelected ? 650 : 450} fontSize="0.75rem" lineHeight={1.25}>
                            {item.label}
                          </Typography>
                        }
                        sx={{ my: 0 }}
                      />
                      {item.notificationCount !== undefined && item.notificationCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                          <NotificationsNoneOutlinedIcon sx={{ fontSize: 18, opacity: 0.95 }} />
                          <Box
                            sx={{
                              minWidth: 18,
                              height: 18,
                              borderRadius: '5px',
                              bgcolor: '#38BDF8',
                              color: '#0B1220',
                              display: 'grid',
                              placeItems: 'center',
                              px: 0.5,
                              fontSize: '0.72rem',
                              fontWeight: 800,
                            }}
                          >
                            {item.notificationCount}
                          </Box>
                        </Box>
                      )}
                      {item.notificationPending && !(item.notificationCount !== undefined && item.notificationCount > 0) && (
                        <NotificationsNoneOutlinedIcon sx={{ fontSize: 18, opacity: 0.72, ml: 0.5 }} />
                      )}
                      {item.badge !== undefined && !hasNotification && (
                        <Box
                          sx={{
                            minWidth: 18,
                            height: 18,
                            borderRadius: '5px',
                            bgcolor: isDark ? '#27272A' : '#E4E4E7',
                            color: chrome.textSecondary,
                            display: 'grid',
                            placeItems: 'center',
                            px: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                          }}
                        >
                          {item.badge}
                        </Box>
                      )}
                      {hasChildren ? (
                        groupOpen
                          ? <KeyboardArrowDownRoundedIcon sx={{ ml: 0.5, fontSize: 15, color: chrome.textMuted }} />
                          : <KeyboardArrowRightRoundedIcon sx={{ ml: 0.5, fontSize: 15, color: chrome.textMuted }} />
                      ) : null}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              {groupOpen ? (
                <Box sx={{ mb: '3px', ml: '27px', pl: '8px', borderLeft: `1px solid ${chrome.border}` }}>
                  {item.children?.map((child) => {
                    const childSelected = navDestinationIsSelected(currentPath, child.path, child.activePaths);

                    return (
                      <ListItemButton
                        key={child.path}
                        selected={childSelected}
                        component="a" href={child.path} aria-current={childSelected ? 'page' : undefined}
                        onClick={(event) => {
                          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                          event.preventDefault();
                          onNavigate(child.path);
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{
                          minHeight: 28,
                          py: 0,
                          px: '8px',
                          mb: '1px',
                          borderRadius: '5px',
                          color: childSelected ? chrome.navActiveText : chrome.textSecondary,
                          '&:hover': { bgcolor: chrome.navHover, color: chrome.text },
                          '&.Mui-selected': {
                            bgcolor: 'transparent',
                            color: chrome.navActiveText,
                          },
                          '&.Mui-selected:hover': { bgcolor: chrome.navHover },
                        }}
                      >
                        {child.icon && <ListItemIcon sx={{ minWidth: 24, color: 'inherit' }}>{child.icon}</ListItemIcon>}
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{
                            fontSize: '0.7rem',
                            fontWeight: childSelected ? 650 : 450,
                            lineHeight: 1.2,
                            noWrap: true,
                          }}
                          sx={{ my: 0 }}
                        />
                      </ListItemButton>
                    );
                  })}
                </Box>
              ) : null}
            </React.Fragment>
          );
        })}
        {!collapsed && visibleNavItems.length === 0 ? (
          <Typography sx={{ px: '9px', py: 1, fontSize: '0.75rem', color: chrome.textMuted }}>
            No navigation matches
          </Typography>
        ) : null}
      </List>
      {controlsPlacement === 'sidebar' && (
        <Box data-testid="sidebar-account-controls" sx={{ mt: 'auto', px: 1, py: 1, borderTop: `1px solid ${chrome.border}`, flexShrink: 0 }}>
          {accountControls}
        </Box>
      )}

      <Menu
        anchorEl={collapsedNavMenuAnchor}
        open={Boolean(collapsedNavMenuAnchor && activeNavMenuItem)}
        onClose={closeCollapsedNavMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              ml: 0.75,
              minWidth: 164,
              p: 0.5,
              border: `1px solid ${chrome.border}`,
              borderRadius: '8px',
              bgcolor: isDark ? '#111113' : '#FFFFFF',
            },
          },
          list: { 'aria-label': activeNavMenuItem?.label, sx: { py: 0 } },
        }}
      >
        <Typography sx={{ px: 1, py: 0.65, fontSize: '0.66rem', fontWeight: 800, color: chrome.textMuted }}>
          {activeNavMenuItem?.label}
        </Typography>
        {activeNavMenuItem?.children?.map((child) => {
          const childSelected = navDestinationIsSelected(currentPath, child.path, child.activePaths);
          return (
            <MenuItem
              key={child.path}
              selected={childSelected}
              component="a" href={child.path} aria-current={childSelected ? 'page' : undefined}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                closeCollapsedNavMenu();
                onNavigate(child.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{ minHeight: 32, px: 1, borderRadius: '5px', fontSize: '0.72rem', fontWeight: childSelected ? 700 : 500 }}
            >
              {child.icon && <ListItemIcon sx={{ minWidth: 24, color: 'inherit' }}>{child.icon}</ListItemIcon>}
              {child.label}
            </MenuItem>
          );
        })}
      </Menu>

    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: viewportHeight, overflow: 'hidden', bgcolor: 'background.default' }}>
      {/* ── Desktop permanent drawer ───────────────────────────────────────── */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{
            width: sidebarW,
            flexShrink: 0,
            height: viewportHeight,
            transition: 'width 0.2s ease',
            borderRight: `1px solid ${isDark ? chrome.border : colors.light.border}`,
            background: isDark
              ? colors.dark.sidebar
              : colors.light.sidebar,
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* ── Mobile temp drawer ─────────────────────────────────────────────── */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              bgcolor: isDark ? colors.dark.sidebar : colors.light.sidebar,
              background: isDark
                ? colors.dark.sidebar
                : colors.light.sidebar,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        <Box
          component="header"
          sx={{
            height: 50,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, md: 1.25 },
            px: { xs: 1, md: 1.5 },
            borderBottom: `1px solid ${isDark ? colors.dark.border : colors.light.border}`,
            bgcolor: isDark ? colors.dark.sidebar : '#FFFFFF',
          }}
        >
          {isMobile ? (
            <>
            <IconButton
              size="small"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
              sx={{ width: 32, height: 32, borderRadius: '7px' }}
            >
              <MenuIcon sx={{ fontSize: 19 }} />
            </IconButton>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}>
              <PaddiqLogo variant="sidebar" compact iconSrc={brandIconSrc} />
            </Box>
            </>
          ) : null}

          <Box
            ref={headerSearchRef}
            onFocus={(event) => setHeaderSearchAnchor(event.currentTarget)}
            sx={{
              width: { xs: 'auto', sm: 320, lg: 400 },
              minWidth: 0,
              height: 32,
              flex: { xs: 1, sm: '0 1 auto' },
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              borderRadius: '7px',
              border: `1px solid ${chrome.border}`,
              bgcolor: isDark ? '#111113' : '#F8F8FA',
              color: chrome.textMuted,
              '&:focus-within': { borderColor: isDark ? '#52525B' : '#A1A1AA', bgcolor: isDark ? '#141416' : '#FFFFFF' },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            <InputBase
              value={headerQuery}
              onChange={(event) => {
                const query = event.target.value;
                setHeaderQuery(query);
                setHeaderSearchAnchor(headerSearchRef.current);
                onGlobalSearchQueryChange?.(query);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && headerSearchResults[0]) {
                  navigateFromSearch(headerSearchResults[0].path, headerSearchResults[0].external);
                }
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  document.getElementById(searchResultsId)?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
                }
                if (event.key === 'Escape') {
                  setHeaderQuery('');
                  onGlobalSearchQueryChange?.('');
                  setHeaderSearchAnchor(null);
                  event.currentTarget.blur();
                }
              }}
              placeholder="Search Paddiq…"
              inputProps={{
                'aria-label': 'Search all of Paddiq',
                'aria-haspopup': 'menu',
                'aria-expanded': Boolean(headerSearchAnchor && normalizedHeaderQuery),
                'aria-controls': headerSearchAnchor && normalizedHeaderQuery ? searchResultsId : undefined,
              }}
              sx={{ minWidth: 0, flex: 1, color: chrome.text, fontSize: '0.75rem', '& input::placeholder': { color: chrome.textMuted, opacity: 1 } }}
            />
            <Typography aria-hidden sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.6rem', color: chrome.textMuted }}>/</Typography>
          </Box>

          {controlsPlacement === 'header' ? accountControls : null}
        </Box>

        <Popper
          anchorEl={headerSearchAnchor}
          open={Boolean(headerSearchAnchor && normalizedHeaderQuery)}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <ClickAwayListener onClickAway={(event) => {
            if (event.target instanceof Node && !headerSearchAnchor?.contains(event.target)) setHeaderSearchAnchor(null);
          }}>
            <Paper elevation={4} sx={{ mt: 0.5, width: { xs: 280, sm: 400 }, maxWidth: 'calc(100vw - 24px)', maxHeight: 'min(600px, calc(100dvh - 80px))', overflowY: 'auto', p: 0.5, border: `1px solid ${chrome.border}`, borderRadius: '8px', bgcolor: chrome.menuBg }}>
              <MenuList id={searchResultsId} aria-label="Search results" sx={{ py: 0 }} onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setHeaderSearchAnchor(null);
                  setHeaderQuery('');
                  onGlobalSearchQueryChange?.('');
                  headerSearchAnchor?.querySelector('input')?.focus();
                }
                if (event.key === 'Tab') setHeaderSearchAnchor(null);
              }}>
          {globalSearchError ? (
            <Typography role="status" sx={{ px: 1, py: 0.75, fontSize: '0.72rem', color: chrome.textMuted }}>{globalSearchError}</Typography>
          ) : globalSearchLoading ? (
            <Typography sx={{ px: 1, py: 0.75, fontSize: '0.72rem', color: chrome.textMuted }}>Searching everything…</Typography>
          ) : headerSearchResults.length ? headerSearchResults.map((result, index) => {
            const showGroup = index === 0 || headerSearchResults[index - 1]?.group !== result.group;
            return (
              <React.Fragment key={`${result.id}:${result.path}`}>
                {showGroup ? (
                  <Typography sx={{ px: 1, pt: index === 0 ? 0.5 : 1, pb: 0.3, fontSize: '0.61rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: chrome.textMuted }}>
                    {result.group}
                  </Typography>
                ) : null}
                <MenuItem onClick={() => navigateFromSearch(result.path, result.external)} sx={{ minHeight: 40, px: 1, gap: 1, borderRadius: '6px' }}>
                  <ListItemIcon sx={{ minWidth: 24, color: chrome.textSecondary, '& svg': { fontSize: 16 } }}>{result.icon}</ListItemIcon>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 650 }}>{result.label}</Typography>
                    {result.description ? <Typography noWrap sx={{ fontSize: '0.65rem', color: chrome.textMuted }}>{result.description}</Typography> : null}
                  </Box>
                </MenuItem>
              </React.Fragment>
            );
          }) : (
            <Typography sx={{ px: 1, py: 0.75, fontSize: '0.72rem', color: chrome.textMuted }}>
              {normalizedHeaderQuery.length < 2 ? 'Type at least 2 characters' : 'No results found'}
            </Typography>
          )}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>

        {showAccountMenu ? (
          <Menu
            anchorEl={accountMenuAnchor}
            open={accountMenuOpen}
            onClose={closeAccountMenu}
            anchorOrigin={{ vertical: controlsPlacement === 'sidebar' ? 'top' : 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: controlsPlacement === 'sidebar' ? 'bottom' : 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5, borderRadius: '8px', border: `1px solid ${chrome.border}`, bgcolor: chrome.menuBg, color: chrome.menuText } } }}
          >
            <Box sx={{ px: 1.5, py: 1.15, borderBottom: `1px solid ${chrome.border}` }} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 850 }}>{activeDriver.name}</Typography>
              <Typography sx={{ mt: 0.15, fontSize: '0.66rem', color: chrome.textSecondary }}>{activeDriver.role}</Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 1.15, borderBottom: `1px solid ${chrome.border}` }} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: chrome.textSecondary }}>Appearance</Typography>
              <ThemeModeSelect />
            </Box>
            {defaultAccountMenuItems.map((item) => (
              <MenuItem key={item.id} onClick={() => handleAccountMenuSelect(item.id)} sx={{ minHeight: 34, fontSize: '0.75rem', gap: 1 }}>
                {accountMenuIcon(item.id)}
                {item.label}
              </MenuItem>
            ))}
            {onLogout ? [
              <Divider key="logout-divider" sx={{ borderColor: chrome.border }} />,
              <MenuItem key="logout" onClick={handleLogoutClick} sx={{ minHeight: 34, fontSize: '0.75rem', gap: 1, color: isDark ? '#FCA5A5' : '#DC2626' }}>
                <LogoutOutlinedIcon sx={{ fontSize: 16 }} />
                {logoutLabel}
              </MenuItem>,
            ] : null}
          </Menu>
        ) : null}

        <Box component="main" ref={mainContentRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1.5, md: 2 }, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
